import {
  ColumnWidthOutlined, DownOutlined, DownloadOutlined, ExpandOutlined, FullscreenExitOutlined, FullscreenOutlined, PrinterOutlined, RotateRightOutlined,
  UpOutlined, ZoomInOutlined, ZoomOutOutlined,
} from "@ant-design/icons";
import React, { type CSSProperties, type ReactNode, type RefObject } from "react";
import { ActionMenu, Button, Flex, Input, Select, Spin, Tooltip, Typography } from "../ui";
import type { ActionMenuItem } from "../ui";
import { useI18nOptional } from "../i18n/useI18n";
import { DocumentAnnotationLayer, type DocumentAnnotation } from "./DocumentAnnotations";
import { rotateDocumentAnnotation } from "./documentAnnotationGeometry";
import type { DocumentTextAnnotation } from "./documentTextAnnotations";
import type { DocumentViewerKind } from "./documentViewerKind";
import { documentViewerDe, documentViewerEn, type DocumentViewerTextKey } from "./documentViewerTranslations";
import {
  DOCUMENT_VIEWER_MAX_FIT_ZOOM, DOCUMENT_VIEWER_MAX_ZOOM, DOCUMENT_VIEWER_MIN_ZOOM, DOCUMENT_VIEWER_ZOOM_STEPS,
  readStoredDocumentZoom, stepDocumentZoom, writeStoredDocumentZoom, type DocumentViewerZoom,
} from "./documentViewerZoom";
import { HtmlDocumentPreview } from "./HtmlDocumentPreview";
import { PdfInlinePreview } from "./PdfInlinePreview";
import { FULL_TURN_DEGREES, HALF_TURN_DEGREES, QUARTER_TURN_DEGREES } from "./documentRotation";
import "./documentViewer.css";

/** Unterhalb dieser Breite des Viewers selbst (nicht des Fensters) zeigt die Leiste nur Zoom, Seite und `⋯`. */
const COMPACT_VIEWER_WIDTH = 768;
const PERCENT = 100;
const ZOOM_EPSILON = 0.01;
const NATURAL_WIDTH_TOLERANCE_PX = 2;
const MENU_ACTION = { rotate: "rotate", download: "download", print: "print", fullscreen: "fullscreen" } as const;

export interface DocumentViewerProps {
  /** Inhaltsart; ableitbar mit `detectDocumentKind`. Office-Dateien liefert der Aufrufer bereits als PDF. */
  kind: DocumentViewerKind;
  title: string;

  /** PDF: Bytes oder URL. */
  fileData?: Uint8Array;
  fileUrl?: string;
  /** PDF und Bild: Markierungen als Anteile der angezeigten Seite; `textAnnotations` werden im PDF-Text gesucht. */
  annotations?: readonly DocumentAnnotation[];
  textAnnotations?: readonly DocumentTextAnnotation[];
  selectedAnnotationId?: string | null;
  onAnnotationSelect?: (id: string) => void;
  /** Bild: darstellbare Adresse (Object-URL, Data-URL). */
  imageSrc?: string;
  /** Text/XML: Inhalt; `textHighlight` wird markiert, wenn er genau einmal vorkommt. */
  text?: string;
  textHighlight?: string;
  /** HTML: Dokument samt eigener Farben; `loading`/`failed` gehören der HTML-Anzeige. */
  html?: string;
  keepDocumentColors?: boolean;
  htmlFrameRef?: RefObject<HTMLIFrameElement | null>;

  /** Der Aufrufer lädt das Dokument noch (PDF, Bild, Text) bzw. das HTML. */
  loading?: boolean;
  failed?: boolean;
  /** Text der Ladeanzeige; ohne Angabe der Standardtext aus den Shared-Übersetzungen. */
  loadingText?: string;
  /** Fehlertext; ohne Angabe der Standardtext aus den Shared-Übersetzungen. */
  errorText?: string;
  /** Eigene Ladeanzeige, z. B. die Flächenanzeige des Produkts; ohne Angabe ein Spinner. */
  renderLoading?: (message: string) => ReactNode;

  /** Herunterladen und Drucken erscheinen nur, wenn der Aufrufer den Handler liefert. */
  onDownload?: () => void;
  downloadDisabled?: boolean;
  onPrint?: () => void;
  printDisabled?: boolean;

  /** Schlüssel, unter dem die Zoomwahl je Gerät gemerkt wird; ohne Schlüssel wird nichts gespeichert. */
  zoomStorageKey?: string;
  defaultZoom?: DocumentViewerZoom;
  /** Texte aus dem Wörterbuch des Produkts; ohne Angabe die mitgelieferten Shared-Texte. */
  translate?: (key: DocumentViewerTextKey) => string;
  onReadyChange?: (ready: boolean) => void;
  className?: string;
}

function useViewerText(translate: DocumentViewerProps["translate"]) {
  const i18n = useI18nOptional();
  return React.useCallback((key: DocumentViewerTextKey): string => {
    if (translate) return translate(key);
    const fromProvider = i18n?.tr(key);
    if (fromProvider && fromProvider !== key) return fromProvider;
    const lang = i18n?.lang ?? (typeof document === "undefined" ? "" : document.documentElement.lang);
    return (lang.toLowerCase().startsWith("de") ? documentViewerDe : documentViewerEn)[key];
  }, [i18n, translate]);
}

function useElementWidth(ref: RefObject<HTMLElement | null>) {
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => setWidth(element.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function innerSize(element: HTMLElement) {
  const style = getComputedStyle(element);
  return {
    width: Math.max(0, element.clientWidth - (Number.parseFloat(style.paddingLeft) || 0) - (Number.parseFloat(style.paddingRight) || 0)),
    height: Math.max(0, element.clientHeight - (Number.parseFloat(style.paddingTop) || 0) - (Number.parseFloat(style.paddingBottom) || 0)),
  };
}

function useElementInnerSize(ref: RefObject<HTMLElement | null>, active: boolean) {
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !active) return;
    const update = () => setSize(previous => {
      const next = innerSize(element);
      return previous.width === next.width && previous.height === next.height ? previous : next;
    });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, active]);
  return size;
}

function ImagePane({ src, title, zoom, rotation, errorText, annotations, selectedAnnotationId, onAnnotationSelect, loadingContent, onEffectiveZoomChange, onReadyChange }: {
  src: string; title: string; zoom: DocumentViewerZoom; rotation: number; errorText: string; loadingContent: ReactNode;
  annotations?: readonly DocumentAnnotation[]; selectedAnnotationId?: string | null; onAnnotationSelect?: (id: string) => void;
  onEffectiveZoomChange: (zoom: number) => void; onReadyChange?: (ready: boolean) => void;
}) {
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = React.useState<{ src: string; width: number; height: number } | null>(null);
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const natural = loaded?.src === src ? loaded : null;
  const failed = failedSrc === src;
  const available = useElementInnerSize(stageRef, natural != null);
  const turned = rotation % HALF_TURN_DEGREES !== 0;
  let scale = typeof zoom === "number" ? zoom : 1;
  if (natural && typeof zoom !== "number" && available.width > 0) {
    const width = turned ? natural.height : natural.width, height = turned ? natural.width : natural.height;
    const toWidth = available.width / width;
    scale = Math.min(zoom === "fitPage" && available.height > 0 ? Math.min(toWidth, available.height / height) : toWidth, DOCUMENT_VIEWER_MAX_FIT_ZOOM);
  }
  React.useEffect(() => { if (natural) onEffectiveZoomChange(scale); }, [natural, scale, onEffectiveZoomChange]);
  React.useEffect(() => {
    onReadyChange?.(natural != null);
    return () => onReadyChange?.(false);
  }, [natural, onReadyChange]);

  if (failed) return <Flex align="center" justify="center" className="mq-document-message"><Typography.Text type="secondary">{errorText}</Typography.Text></Flex>;
  const boxWidth = natural ? (turned ? natural.height : natural.width) * scale : 0;
  const boxHeight = natural ? (turned ? natural.width : natural.height) * scale : 0;
  const shown = (annotations ?? []).map(item => rotateDocumentAnnotation(item, rotation));
  return <>
    {!natural && loadingContent}
    {/* Das Bild wird zuerst unsichtbar geladen, damit Maße und Einpassung vor dem ersten Zeichnen feststehen. */}
    {!natural && <img src={src} alt="" hidden onLoad={event => setLoaded({ src, width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} onError={() => setFailedSrc(src)} />}
    {natural && <div ref={stageRef} className="mq-document-image-stage" data-zoom-mode={typeof zoom === "number" ? "fixed" : "fit"} role="region" aria-label={title} tabIndex={0}>
      <div className="mq-document-image-box" style={{ width: boxWidth, height: boxHeight }}>
        <div className="mq-document-image-inner" style={{ width: natural.width * scale, height: natural.height * scale, transform: `translate(-50%, -50%) rotate(${rotation}deg)` }}>
          <img className="mq-document-image" src={src} alt={title} draggable={false} />
        </div>
        {shown.length > 0 && <DocumentAnnotationLayer annotations={shown} page={1} selectedId={selectedAnnotationId} onSelect={onAnnotationSelect} />}
      </div>
    </div>}
  </>;
}

function TextPane({ text, highlight, title, zoom, onEffectiveZoomChange, onReadyChange }: {
  text: string; highlight?: string; title: string; zoom: DocumentViewerZoom;
  onEffectiveZoomChange: (zoom: number) => void; onReadyChange?: (ready: boolean) => void;
}) {
  const markRef = React.useRef<HTMLElement | null>(null);
  const factor = typeof zoom === "number" ? zoom : 1;
  const at = highlight ? text.indexOf(highlight) : -1;
  const unique = at >= 0 && text.indexOf(highlight!, at + highlight!.length) < 0;
  React.useEffect(() => { onEffectiveZoomChange(factor); }, [factor, onEffectiveZoomChange]);
  React.useEffect(() => { onReadyChange?.(true); return () => onReadyChange?.(false); }, [onReadyChange]);
  React.useEffect(() => { if (unique) markRef.current?.scrollIntoView?.({ block: "nearest" }); }, [unique, highlight]);
  return <div className="mq-document-text-scroll" role="region" aria-label={title} tabIndex={0}>
    <pre className="mq-document-text" style={{ "--mq-document-zoom": factor } as CSSProperties}>
      {unique ? <>{text.slice(0, at)}<mark ref={markRef} className="mq-document-text-match">{highlight}</mark>{text.slice(at + highlight!.length)}</> : text}
    </pre>
  </div>;
}

function HtmlPane({ html, title, zoom, keepDocumentColors, loading, failed, frameRef, loadingText, errorText, onEffectiveZoomChange, onReadyChange }: {
  html: string; title: string; zoom: DocumentViewerZoom; keepDocumentColors?: boolean; loading?: boolean; failed?: boolean;
  frameRef?: RefObject<HTMLIFrameElement | null>; loadingText: string; errorText: string;
  onEffectiveZoomChange: (zoom: number) => void; onReadyChange?: (ready: boolean) => void;
}) {
  const ownFrameRef = React.useRef<HTMLIFrameElement | null>(null);
  const activeFrame = frameRef ?? ownFrameRef;
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const available = useElementWidth(stageRef);
  // Natürliche Breite: nur bekannt, wenn das Dokument bei der gemessenen Skalierung überlief.
  const [naturalWidth, setNaturalWidth] = React.useState<{ html: string; width: number } | null>(null);
  const natural = naturalWidth?.html === html ? naturalWidth.width : null;
  const scale = typeof zoom === "number" ? zoom : natural && available > 0 ? Math.min(1, available / natural) : 1;
  const scaleRef = React.useRef(scale);
  scaleRef.current = scale;
  const ready = React.useRef(false);

  const measure = React.useCallback(() => {
    const root = activeFrame.current?.contentDocument?.documentElement;
    if (!root || !ready.current || root.scrollWidth <= root.clientWidth + 1) return;
    const width = root.scrollWidth / scaleRef.current;
    setNaturalWidth(previous => previous?.html === html && Math.abs(previous.width - width) < NATURAL_WIDTH_TOLERANCE_PX ? previous : { html, width });
  }, [activeFrame, html]);

  React.useEffect(() => { onEffectiveZoomChange(scale); }, [scale, onEffectiveZoomChange]);
  const handleReady = React.useCallback((value: boolean) => {
    ready.current = value;
    onReadyChange?.(value);
    if (value) requestAnimationFrame(measure);
  }, [measure, onReadyChange]);
  React.useEffect(() => { if (ready.current) requestAnimationFrame(measure); }, [available, measure]);

  return <div ref={stageRef} className="mq-document-html-stage">
    <HtmlDocumentPreview html={html} title={title} scale={scale} loading={loading} failed={failed} loadingText={loadingText} errorText={errorText}
      frameRef={activeFrame} onReadyChange={handleReady} keepDocumentColors={keepDocumentColors} />
  </div>;
}

/**
 * Gemeinsame Dokumentansicht: PDF, Bild, Text/XML und HTML mit Leiste für Seite, Zoom, Drehen, Herunterladen,
 * Drucken und Vollbild. Standard ist „an Breite anpassen“ ohne waagerechten Rollbalken; die Zoomwahl wird über
 * `zoomStorageKey` je Gerät gemerkt. Dateizugriff, Ladeanzeige und Aktionen liefert der Aufrufer.
 */
export function DocumentViewer(props: DocumentViewerProps) {
  const { kind, title, loading = false, failed = false, renderLoading, onDownload, downloadDisabled, onPrint, printDisabled, zoomStorageKey, defaultZoom = "fitWidth", onReadyChange, className } = props;
  const tr = useViewerText(props.translate);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const width = useElementWidth(rootRef);
  const compact = width > 0 && width < COMPACT_VIEWER_WIDTH;
  const paged = kind === "pdf";
  const rotatable = kind === "pdf" || kind === "image";
  const [zoom, setZoom] = React.useState<DocumentViewerZoom>(() => readStoredDocumentZoom(zoomStorageKey) ?? defaultZoom);
  const [effectiveZoom, setEffectiveZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pageCount, setPageCount] = React.useState(0);
  const [pageDraft, setPageDraft] = React.useState<string | null>(null);
  const [pageRequest, setPageRequest] = React.useState<{ page: number; key: number } | null>(null);
  const [fullscreen, setFullscreen] = React.useState(false);
  const fullscreenAvailable = typeof document !== "undefined" && document.fullscreenEnabled !== false;

  // Drehung und Seite gehören zum angezeigten Dokument: beim Wechsel zurücksetzen (die Zoomwahl bleibt).
  const documentKey = props.fileData ?? props.fileUrl ?? props.imageSrc ?? title;
  const [shownDocument, setShownDocument] = React.useState(documentKey);
  if (shownDocument !== documentKey) {
    setShownDocument(documentKey);
    setRotation(0);
    setPage(1);
    setPageCount(0);
    setPageRequest(null);
  }

  React.useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const changeZoom = React.useCallback((next: DocumentViewerZoom) => {
    setZoom(next);
    writeStoredDocumentZoom(zoomStorageKey, next);
  }, [zoomStorageKey]);
  const goToPage = (target: number) => {
    const clamped = Math.min(Math.max(1, Math.round(target)), Math.max(1, pageCount));
    setPage(clamped);
    setPageDraft(null);
    setPageRequest(previous => ({ page: clamped, key: (previous?.key ?? 0) + 1 }));
  };
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void rootRef.current?.requestFullscreen?.();
  };
  const handlePage = React.useCallback((value: number) => { setPage(value); setPageDraft(null); }, []);
  const handleZoom = React.useCallback((value: number) => setEffectiveZoom(value), []);

  const errorText = props.errorText ?? tr("DocumentViewer.error");
  const loadingMessage = props.loadingText ?? tr("DocumentViewer.loading");
  const loadingContent = renderLoading ? renderLoading(loadingMessage) : <Flex align="center" justify="center" gap="small" className="mq-document-message" role="status"><Spin /><Typography.Text type="secondary">{loadingMessage}</Typography.Text></Flex>;
  const message = (content: ReactNode) => <div className={`mq-document-viewer ${className ?? ""}`} ref={rootRef}><div className="mq-document-viewer-body">{content}</div></div>;
  if (failed && kind !== "html") return message(<Flex align="center" justify="center" className="mq-document-message"><Typography.Text type="secondary">{errorText}</Typography.Text></Flex>);
  if (loading && kind !== "html") return message(loadingContent);

  const zoomOptions = [
    { value: "fitWidth", label: tr("DocumentViewer.fitWidth") },
    ...(kind === "pdf" || kind === "image" ? [{ value: "fitPage", label: tr("DocumentViewer.fitPage") }] : []),
    ...DOCUMENT_VIEWER_ZOOM_STEPS.map(step => ({ value: step, label: `${Math.round(step * PERCENT)} %` })),
  ];
  const percent = `${Math.round(effectiveZoom * PERCENT)} %`;
  const canZoomOut = effectiveZoom > DOCUMENT_VIEWER_MIN_ZOOM + ZOOM_EPSILON;
  const canZoomIn = effectiveZoom < DOCUMENT_VIEWER_MAX_ZOOM - ZOOM_EPSILON;
  const shownPage = pageDraft ?? String(page);
  const pageOf = tr("DocumentViewer.pageOf").replace("{total}", String(pageCount));
  const rotate = () => setRotation(value => (value + QUARTER_TURN_DEGREES) % FULL_TURN_DEGREES);

  const fitTarget: DocumentViewerZoom = zoom === "fitWidth" && (kind === "pdf" || kind === "image") ? "fitPage" : "fitWidth";
  const iconButton = (label: string, icon: ReactNode, onClick: () => void, disabled?: boolean) =>
    <Tooltip title={label}><Button type="text" size="small" icon={icon} aria-label={label} disabled={disabled} onClick={onClick} /></Tooltip>;
  const zoomControls = <div className="mq-document-viewer-group" role="group" aria-label={tr("DocumentViewer.zoom")}>
    <Tooltip title={tr("DocumentViewer.zoomOut")}><Button type="text" size="small" icon={<ZoomOutOutlined />} aria-label={tr("DocumentViewer.zoomOut")} disabled={!canZoomOut} onClick={() => changeZoom(stepDocumentZoom(effectiveZoom, -1))} /></Tooltip>
    <Select className="mq-document-viewer-zoom" size="small" showSearch={false} popupMatchSelectWidth={false} aria-label={tr("DocumentViewer.zoom")} value={zoom} options={zoomOptions}
      labelRender={({ label, value }) => typeof value === "number" || !compact ? label : percent}
      onChange={value => changeZoom(value as DocumentViewerZoom)} />
    <Tooltip title={tr("DocumentViewer.zoomIn")}><Button type="text" size="small" icon={<ZoomInOutlined />} aria-label={tr("DocumentViewer.zoomIn")} disabled={!canZoomIn} onClick={() => changeZoom(stepDocumentZoom(effectiveZoom, 1))} /></Tooltip>
    {/* Einpassen mit einem Klick: auf Breite, bei bereits eingepasster Breite (PDF/Bild) auf die ganze Seite. */}
    {iconButton(tr(fitTarget === "fitWidth" ? "DocumentViewer.fitWidth" : "DocumentViewer.fitPage"), fitTarget === "fitWidth" ? <ColumnWidthOutlined /> : <ExpandOutlined />, () => changeZoom(fitTarget))}
  </div>;

  const pageControls = paged && pageCount > 0 ? <div className="mq-document-viewer-group" role="group" aria-label={tr("DocumentViewer.page")}>
    <Tooltip title={tr("DocumentViewer.previousPage")}><Button type="text" size="small" icon={<UpOutlined />} aria-label={tr("DocumentViewer.previousPage")} disabled={page <= 1} onClick={() => goToPage(page - 1)} /></Tooltip>
    {compact ? <span className="mq-document-viewer-pagecount" aria-label={`${tr("DocumentViewer.page")} ${page} ${pageOf}`}>{`${page} / ${pageCount}`}</span> : <><span className="mq-document-viewer-pageinput"><Input size="small" inputMode="numeric" aria-label={tr("DocumentViewer.page")} value={shownPage}
      onChange={event => setPageDraft(event.target.value.replace(/\D/g, ""))}
      onFocus={event => event.target.select()}
      onBlur={() => { if (pageDraft != null) goToPage(Number(pageDraft) || page); }}
      onPressEnter={() => goToPage(Number(pageDraft ?? page) || page)} /></span>
    <span className="mq-document-viewer-pagecount">{pageOf}</span></>}
    <Tooltip title={tr("DocumentViewer.nextPage")}><Button type="text" size="small" icon={<DownOutlined />} aria-label={tr("DocumentViewer.nextPage")} disabled={page >= pageCount} onClick={() => goToPage(page + 1)} /></Tooltip>
  </div> : null;

  const menuItems: ActionMenuItem[] = [
    ...(rotatable ? [{ key: MENU_ACTION.rotate, label: tr("DocumentViewer.rotate"), icon: <RotateRightOutlined /> }] : []),
    ...(onDownload ? [{ key: MENU_ACTION.download, label: tr("DocumentViewer.download"), icon: <DownloadOutlined />, disabled: downloadDisabled }] : []),
    ...(onPrint ? [{ key: MENU_ACTION.print, label: tr("DocumentViewer.print"), icon: <PrinterOutlined />, disabled: printDisabled }] : []),
    ...(fullscreenAvailable ? [{ key: MENU_ACTION.fullscreen, label: tr(fullscreen ? "DocumentViewer.exitFullscreen" : "DocumentViewer.fullscreen"), icon: fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined /> }] : []),
  ];
  const runMenuAction = (key: string) => {
    if (key === MENU_ACTION.rotate) rotate();
    if (key === MENU_ACTION.download) onDownload?.();
    if (key === MENU_ACTION.print) onPrint?.();
    if (key === MENU_ACTION.fullscreen) toggleFullscreen();
  };

  const content = kind === "pdf"
    ? <PdfInlinePreview fileData={props.fileData} fileUrl={props.fileUrl} title={title} loadingContent={loadingContent} errorText={errorText} zoom={zoom} rotation={rotation}
      annotations={props.annotations} textAnnotations={props.textAnnotations} selectedAnnotationId={props.selectedAnnotationId} onAnnotationSelect={props.onAnnotationSelect}
      pageRequest={pageRequest} onPageCountChange={setPageCount} onCurrentPageChange={handlePage} onEffectiveZoomChange={handleZoom} onReadyChange={onReadyChange} />
    : kind === "image"
      ? <ImagePane src={props.imageSrc ?? ""} title={title} zoom={zoom} rotation={rotation} errorText={errorText} loadingContent={loadingContent}
        annotations={props.annotations} selectedAnnotationId={props.selectedAnnotationId} onAnnotationSelect={props.onAnnotationSelect}
        onEffectiveZoomChange={handleZoom} onReadyChange={onReadyChange} />
      : kind === "text"
        ? <TextPane text={props.text ?? ""} highlight={props.textHighlight} title={title} zoom={zoom} onEffectiveZoomChange={handleZoom} onReadyChange={onReadyChange} />
        : <HtmlPane html={props.html ?? ""} title={title} zoom={zoom} keepDocumentColors={props.keepDocumentColors} loading={loading} failed={failed} frameRef={props.htmlFrameRef}
          loadingText={loadingMessage} errorText={errorText} onEffectiveZoomChange={handleZoom} onReadyChange={onReadyChange} />;

  return <div ref={rootRef} className={`mq-document-viewer ${className ?? ""}`} data-compact={compact || undefined} data-kind={kind}>
    <div className="mq-document-viewer-bar" role="toolbar" aria-label={title}>
      {compact ? <>
        {zoomControls}
        {pageControls}
        <span className="mq-document-viewer-group" data-end>
          <ActionMenu type="text" size="small" aria-label={tr("DocumentViewer.more")} items={menuItems} onAction={runMenuAction} disabled={menuItems.length === 0} />
        </span>
      </> : <>
        {pageControls}
        {zoomControls}
        {rotatable && iconButton(tr("DocumentViewer.rotate"), <RotateRightOutlined />, rotate)}
        <span className="mq-document-viewer-group" data-end>
          {onDownload && iconButton(tr("DocumentViewer.download"), <DownloadOutlined />, onDownload, downloadDisabled)}
          {onPrint && iconButton(tr("DocumentViewer.print"), <PrinterOutlined />, onPrint, printDisabled)}
          {fullscreenAvailable && iconButton(tr(fullscreen ? "DocumentViewer.exitFullscreen" : "DocumentViewer.fullscreen"), fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />, toggleFullscreen)}
        </span>
      </>}
    </div>
    <div className="mq-document-viewer-body">{content}</div>
  </div>;
}
